/** Partner approvals (C03-001/002) and staff RBAC via Entra (C04-003/004). */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { CHECKLIST_ITEMS } from '@qm/contracts/partners';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Notice, ReasonForm, Screen, Tabs, When } from '../kit/ui';
import type { ApplicationView, RoleMapEntry } from '../api';
import { STAFF_ROLES, approvalGate, canSupport, isSuperAdmin } from '../lib/logic';
import { useAdminApi } from './layout';
import { ViewAsAction } from './view-as';

export function ApplicationsPage() {
  const { t } = useSurface();
  const api = useAdminApi();
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? 'pending';
  const list = useResource(`apps:${status}`, () => api.applications(status));
  return (
    <Screen title={t('partners.admin.queue.title')} testId="applications">
      <Tabs label={t('partners.admin.queue.title')} active={status} onSelect={(v) => setSp({ status: v })} tabs={(['pending', 'approved', 'rejected', 'suspended'] as const).map((s) => ({ id: s, label: t(`partners.status.${s}`) }))} />
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(a) => a.id} empty={t('portal.kit.empty')} columns={[
          { key: 'c', header: t('portal.admin.partners.company'), render: (a) => <Link to={`/partners/${encodeURIComponent(a.id)}`}>{a.companyName}</Link> },
          { key: 'd', header: t('portal.admin.partners.domain'), render: (a) => a.companyDomain },
          { key: 's', header: t('portal.admin.status'), render: (a) => <Badge>{t(`partners.status.${a.status}`)}</Badge> },
          { key: 't', header: t('portal.admin.partners.applied'), narrow: 'hide', render: (a) => <When iso={a.createdAt} /> },
        ]} />}
      </Loadable>
      {id ? <ApplicationPanel id={id} onChanged={list.reload} /> : null}
    </Screen>
  );
}

function ApplicationPanel({ id, onChanged }: { id: string; onChanged: () => void }) {
  const api = useAdminApi();
  const app = useResource(`app:${id}`, () => api.application(id));
  return <Loadable resource={app}>{(a) => <ApplicationReview app={a} onChanged={() => { app.reload(); onChanged(); }} />}</Loadable>;
}

/** Checklist must be complete and marked ready before the Super Admin approve control enables. Decisions are final. */
export function ApplicationReview({ app, onChanged }: { app: ApplicationView; onChanged: () => void }) {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const gate = approvalGate(app, roles);
  const check = useAction((item: (typeof CHECKLIST_ITEMS)[number], v: boolean) => api.setChecklist(app.id, item, v));
  const ready = useAction(() => api.markReady(app.id));
  const decide = useAction((d: 'approve' | 'reject' | 'suspend' | 'convert', reason: string) => api.decide(app.id, d, reason));
  const finding = useAction((isMarketing: boolean, note: string) => api.setMarketingFinding(app.id, isMarketing, note));
  const [isMarketing, setIsMarketing] = useState<boolean | null>(app.isMarketingCompany ?? null);
  const done = async (p: Promise<unknown>) => { if (await p) onChanged(); };
  return (
    <Panel data-testid="application-review" data-app={app.id}>
      <Heading level={2}>{app.companyName}</Heading>
      <Text>{app.contactName} - {app.contactEmail} - {app.country}</Text>
      <dl>
        {app.contactPhone?<><dt>{t('partners.apply.contactPhone')}</dt><dd><bdi>{app.contactPhone}</bdi></dd></>:null}
        {app.designation?<><dt>{t('partners.apply.designation')}</dt><dd>{app.designation}</dd></>:null}
        {app.city?<><dt>{t('partners.apply.city')}</dt><dd>{app.city}</dd></>:null}
        {app.clientCount!==null&&app.clientCount!==undefined?<><dt>{t('partners.apply.clientCountExact')}</dt><dd>{app.clientCount}</dd></>:null}
        {app.website&&/^https?:\/\//i.test(app.website)?<><dt>{t('partners.apply.websiteOptional')}</dt><dd><a href={app.website} target="_blank" rel="noopener noreferrer">{app.website}</a></dd></>:null}
      </dl>
      <Heading level={3}>{t('partners.admin.checklist.title')}</Heading>
      {CHECKLIST_ITEMS.map((i) => (
        <Checkbox key={i} name={i} label={t(`partners.admin.checklist.${i}`)} checked={app.checklist?.[i]?.checked === true}
          disabled={app.status !== 'pending' || !canSupport(roles) || check.pending} onChange={(v) => void done(check.run(i, v))} />
      ))}
      <Heading level={3}>{t('partners.admin.marketing.title')}</Heading>
      <Text tone="muted">{t('partners.admin.marketing.intro')}</Text>
      {app.isMarketingCompany !== null && app.isMarketingCompany !== undefined ? (
        <Text data-testid="marketing-finding">{t('partners.admin.marketing.recorded', { finding: t(app.isMarketingCompany ? 'partners.admin.marketing.yes' : 'partners.admin.marketing.no') })}</Text>
      ) : null}
      {app.status === 'pending' && canSupport(roles) ? (
        <div data-testid="marketing-form">
          <Checkbox name="marketing-yes" label={t('partners.admin.marketing.yes')} checked={isMarketing === true} onChange={(v) => setIsMarketing(v ? true : null)} />
          <Checkbox name="marketing-no" label={t('partners.admin.marketing.no')} checked={isMarketing === false} onChange={(v) => setIsMarketing(v ? false : null)} />
          {isMarketing !== null ? <ReasonForm testId="marketing-note" label={t('partners.admin.marketing.note')} submitLabel={t('partners.admin.marketing.save')} onSubmit={(note) => done(finding.run(isMarketing, note))} /> : null}
        </div>
      ) : null}
      {gate.reason === 'marketing_unknown' ? <Notice tone="warn" testId="marketing-missing">{t('partners.admin.marketing.missing')}</Notice> : null}
      {gate.reason === 'incomplete' ? <Notice tone="warn" testId="checklist-incomplete">{t('partners.errors.checklist_incomplete')}</Notice> : null}
      {gate.canMarkReady ? <Button onClick={() => void done(ready.run())} disabled={ready.pending} data-testid="mark-ready">{t('partners.admin.checklist.markReady')}</Button> : null}
      {gate.reason === 'role' ? <Text tone="muted" data-testid="needs-super-admin">{t('portal.admin.partners.needsSuperAdmin')}</Text> : null}
      {app.status === 'pending' ? (
        <div data-testid="decisions">
          <Text tone="muted">{t('partners.admin.decisionFinal')}</Text>
          {gate.canApprove ? <ReasonForm testId="approve-form" label={t('partners.admin.reason')} submitLabel={t('partners.admin.approve')} onSubmit={(r) => done(decide.run('approve', r))} /> : <Button variant="primary" disabled data-testid="approve-disabled">{t('partners.admin.approve')}</Button>}
          {gate.canConvert ? (
            <div data-testid="convert">
              <Text tone="muted">{t('partners.admin.convertNote')}</Text>
              <ReasonForm testId="convert-form" label={t('partners.admin.reason')} submitLabel={t('partners.admin.convert')} onSubmit={(r) => done(decide.run('convert', r))} />
            </div>
          ) : null}
          {isSuperAdmin(roles) ? <ReasonForm testId="reject-form" danger label={t('partners.admin.reason')} submitLabel={t('partners.admin.reject')} onSubmit={(r) => done(decide.run('reject', r))} /> : null}
        </div>
      ) : null}
      {app.status === 'approved' && isSuperAdmin(roles) ? (
        <div data-testid="suspend">
          <Text tone="muted">{t('portal.admin.partners.suspendNote')}</Text>
          <ReasonForm danger label={t('partners.admin.reason')} submitLabel={t('partners.admin.suspend')} onSubmit={(r) => done(decide.run('suspend', r))} />
        </div>
      ) : null}
      {app.convertedToCustomerAt ? <Badge>{t('partners.admin.converted')}</Badge> : null}
      {app.decisionReason ? <Text data-testid="decision-reason">{t('portal.admin.partners.decided', { reason: app.decisionReason })} <When iso={app.decidedAt ?? null} /></Text> : null}
      <ErrorNote error={check.error ?? ready.error ?? decide.error ?? finding.error} />
      {app.partnerId ? <ViewAsAction kind="agency" level={3} target={{ partnerId: app.partnerId }} /> : null}
    </Panel>
  );
}

export function StaffPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const map = useResource('entra:map', api.roleMap);
  const [userId, setUserId] = useState('');
  const [lookup, setLookup] = useState('');
  const user = useResource(`staff:${lookup}`, () => (lookup ? api.staffUser(lookup) : Promise.resolve(null)));
  const revoke = useAction(api.revokeSessions);
  if (!isSuperAdmin(roles)) return <Screen title={t('portal.admin.staff.title')}><Notice>{t('portal.admin.superAdminOnly')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.staff.title')} subtitle={t('portal.admin.staff.subtitle')} testId="staff">
      <Loadable resource={map}>{(rows) => <RoleMapEditor initial={rows} onSaved={map.reload} />}</Loadable>
      <Panel>
        <Heading level={2}>{t('portal.admin.staff.lookup')}</Heading>
        <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); setLookup(userId.trim()); }}>
          <Field label={t('portal.admin.staff.userId')}>{(id) => <input id={id} value={userId} onChange={(e) => setUserId(e.target.value)} />}</Field>
          <Button type="submit">{t('portal.admin.search')}</Button>
        </form>
        <Loadable resource={user}>
          {(u) => (u ? (
            <div data-testid="staff-user">
              <Text>{u.email} - {u.roles.map((r) => t(`portal.admin.role.${r}`)).join(', ')} - {u.status}</Text>
              <Text tone="muted">{t('portal.admin.staff.lastSync')}: <When iso={u.lastSyncAt ?? null} /></Text>
              <Button onClick={() => void revoke.run(u.userId)} disabled={revoke.pending}>{t('portal.admin.staff.revoke')}</Button>
              {revoke.result ? <Notice testId="sessions-revoked">{t('portal.admin.staff.revoked', { n: revoke.result.revoked })}</Notice> : null}
            </div>
          ) : null)}
        </Loadable>
        <Text tone="muted">{t('portal.admin.staff.propagation')}</Text>
        <ErrorNote error={revoke.error} />
      </Panel>
    </Screen>
  );
}

export function RoleMapEditor({ initial, onSaved }: { initial: RoleMapEntry[]; onSaved: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [rows, setRows] = useState<RoleMapEntry[]>(initial);
  const save = useAction(api.saveRoleMap);
  const remove = useAction(api.removeRoleMapping);
  const valid = rows.every((r) => (r.groupRef ?? '').trim() && (STAFF_ROLES as readonly string[]).includes(r.role));
  return (
    <Panel data-testid="role-map">
      <Heading level={2}>{t('portal.admin.staff.roleMap')}</Heading>
      {rows.map((r, n) => (
        <div key={n} className="qx-toolbar">
          <Field label={t('portal.admin.staff.group')}>{(id) => <input id={id} value={r.groupRef} onChange={(e) => setRows((xs) => xs.map((x, j) => (j === n ? { ...x, groupRef: e.target.value } : x)))} />}</Field>
          <Field label={t('portal.admin.staff.role')}>{(id) => <select id={id} value={r.role} onChange={(e) => setRows((xs) => xs.map((x, j) => (j === n ? { ...x, role: e.target.value } : x)))}>{STAFF_ROLES.map((s) => <option key={s} value={s}>{t(`portal.admin.role.${s}`)}</option>)}</select>}</Field>
          <Button disabled={remove.pending} onClick={async () => { if (r.updatedAt && !(await remove.run(r.groupRef))) return; setRows((xs) => xs.filter((_, j) => j !== n)); }}>{t('portal.admin.remove')}</Button>
        </div>
      ))}
      <Button onClick={() => setRows((xs) => [...xs, { groupRef: '', role: 'staff_readonly' }])}>{t('portal.admin.staff.addMapping')}</Button>
      <Button variant="primary" disabled={!valid || save.pending} onClick={async () => { if (await save.run(rows)) onSaved(); }}>{t('portal.admin.save')}</Button>
      <ErrorNote error={save.error ?? remove.error} />
    </Panel>
  );
}
